import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3, Briefcase, Building2, Calendar, CalendarCheck, CheckCircle2, ClipboardCheck,
  Clock, Coffee, History, Home, Info, Lock, Mail, Play, RefreshCw, Send, Smartphone, Square, Tablet,
  PartyPopper, UserX, WifiOff, LucideIcon,
} from 'lucide-react';
import { useSiteContext } from '../../../SiteContext';
import { Page } from '../../../interfaces/PagesInterface';
import {
  describePortalError,
  getPortalActivity,
  getTokenStatus,
  PUNCH_URL,
  sendPendingPunch,
} from '../../../api/portal';
import { usePortalData } from './PortalContext';
import JobPicker from './JobPicker';
import {
  ActivityItem,
  applyPendingPunch,
  blockedReason,
  buildUpcoming,
  complianceHint,
  DaySummary,
  deriveDay,
  HeroAction,
  heroActions,
  isOverhead,
  isShortMeal,
  jobCode,
  jobSub,
  jobTitle,
  MealRules,
  namedNonJob,
  normalizeRow,
  PENDING_ROW_ID,
  PUNCH_LABEL,
  PunchEvent,
  PunchKind,
  sameJob,
  selectStrip,
  SHORT_MEAL_MESSAGE,
  shortRange,
  shouldRemindMeal,
  SOURCE_LABEL,
  SourceKind,
  todayMealFlags,
  todaysActivity,
  workStateOf,
} from './portalModel';
import {
  clockParts,
  floorToMinute,
  fmtDateLong,
  fmtDayShort,
  fmtHours,
  fmtTime,
  fmtYmdDow,
  greetingFor,
  localMinutes,
  localYmd,
  ymdTile,
} from './portalTime';
import { buildClockInBody, buildClockOutBody, buildSwitchBody, isSwitchableJob } from './punchBodies';
import {
  clearPendingPunch,
  createPendingPunch,
  loadPendingPunch,
  PendingPunch,
  PunchAction,
  savePendingPunch,
} from './punchStore';
import { safeStorage } from './portalSession';
import { punchFailureAction } from './portalErrors';
import { periodContaining } from './timesheetModel';
import type { PortalActivityItem, PortalHourlyRow, PortalJob } from './portalTypes';
import { usePortalToast } from './usePortalToast';
import SrcChip from './SrcChip';
import ActivityRow, { DeltaChip } from './ActivityRow';
import { portalJobMode } from './portalRoleRules';
import './employeePortal.css';

/**
 * Employee Time Portal — Today (P-TAS-20 Phase 3, App-1; handoff §1–2, screenshots 01–14
 * and 45–49). Hourly punching from the browser, mirroring the mobile V2 sequence:
 *   Clock In / Clock Back In   POST   /portal/timeEntryHourly
 *   Clock Out                  PUT    /portal/timeEntryHourly/:id  (whole row)
 *   Start / End Meal, Switch   POST   /portal/timeEntryHourly/:id/switch  (atomic)
 * Each tap is written locally first (punchStore) with its own clientPunchID and the tap
 * time, then sent; a network failure keeps it and retries the SAME body every 15 s, a 401
 * opens the session modal and nothing is sent until the user signs in again.
 * Layout heights are fixed (D7): strip 60, hero 316 (actions 50, hint 20), card row 340.
 */

const RETRY_MS = 15000;
/** Recent activity shows this week's latest N events (handoff: 3; the card holds 5 at 340px). */
const RECENT_ACTIVITY_COUNT = 5;
const CONTINUE_KEY = 'etp.continueHere';
const DAY_START_MIN = 6 * 60;
const DAY_SPAN_MIN = 14 * 60;

const KIND_ICON: Record<PunchKind, LucideIcon> = {
  in: Play,
  out: Square,
  'meal-start': Coffee,
  'meal-end': Coffee,
  'meal2-start': Coffee,
  'meal2-end': Coffee,
};

function actionLabel(action: PunchAction, mealCount: number): string {
  switch (action) {
    case 'clockIn': return 'Clock In';
    case 'clockOut': return 'Clock Out';
    case 'mealStart': return mealCount >= 1 ? '2nd Meal Start' : 'Meal Start';
    case 'mealEnd': return mealCount >= 2 ? '2nd Meal End' : 'Meal End';
    case 'jobSwitch': return 'job switch';
  }
}

/** The session modal's "nothing was lost" note for an unsent punch. */
function punchNote(p: PendingPunch, zone: string): string {
  return `your ${p.label} at ${fmtTime(p.tapMs, zone)} is saved on this device and sends once you're signed in`;
}

function pct(ms: number, zone: string): number {
  return Math.max(0, Math.min(100, ((localMinutes(ms, zone) - DAY_START_MIN) / DAY_SPAN_MIN) * 100));
}

const TodayPage: React.FC = () => {
  const { setPage, userName } = useSiteContext();
  const {
    cfg, zone, self, loading, loadError, thisWeekStatus, lastWeekStatus, lastWeekOpen, previousPeriod, currentPeriod,
    periods, requests, holidays, reload, reloadSelf, sessionExpired, reportSessionExpired, onSessionRestored, openTimesheet,
  } = usePortalData();
  const toast = usePortalToast();
  const showToast = toast.show;
  const storage = useMemo(() => safeStorage('session'), []);

  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const today = localYmd(nowMs, zone);

  const employeeID = self?.employeeID ?? null;
  const [pending, setPendingState] = useState<PendingPunch | null>(null);
  const pendingRef = useRef<PendingPunch | null>(null);
  const setPending = useCallback((p: PendingPunch | null) => {
    pendingRef.current = p;
    setPendingState(p);
  }, []);
  const [retryAt, setRetryAt] = useState<number | null>(null);
  const [nextJob, setNextJob] = useState<PortalJob | null>(null);
  const [continuedRowID, setContinuedRowID] = useState<number | null>(() => {
    const v = Number(storage?.getItem(CONTINUE_KEY) ?? '');
    return Number.isInteger(v) && v > 0 ? v : null;
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [shortMealTap, setShortMealTap] = useState<number | null>(null);
  const clockOutFlagged = useRef<Record<string, boolean>>({});

  // A punch written before a reload (or a closed tab) resumes: retry it right away.
  useEffect(() => {
    if (employeeID == null) return;
    const stored = loadPendingPunch(storage, employeeID);
    if (stored) {
      setPending(stored);
      setRetryAt(Date.now());
    }
  }, [employeeID, storage, setPending]);

  /* ------------------------------------------------ the day */
  const apiRows = useMemo(() => {
    const map = new Map<number, PortalHourlyRow>();
    for (const r of self?.timeEntriesHourly ?? []) map.set(r.timeEntryHourlyID, r);
    if (self?.openPunchHourly) map.set(self.openPunchHourly.timeEntryHourlyID, self.openPunchHourly);
    return map;
  }, [self]);
  const baseRows = useMemo(
    () => Array.from(apiRows.values()).map(normalizeRow).filter((r): r is NonNullable<typeof r> => r != null),
    [apiRows],
  );
  const day: DaySummary = useMemo(
    () => deriveDay(applyPendingPunch(baseRows, pending), nowMs, zone, today),
    [baseRows, pending, nowMs, zone, today],
  );

  const workState = workStateOf(self?.role?.stateAbbr);
  const rules: MealRules = {
    workState,
    waiversOn: workState === 'CA' ? cfg.mealWaivers.ca : workState === 'NV' ? cfg.mealWaivers.nv : false,
    penaltyThresholdMinutes: cfg.mealPenaltyThresholdMinutes,
  };
  const { jobCosting, nonJob } = useMemo(() => portalJobMode(cfg.jobCosting, self?.role), [cfg.jobCosting, self?.role]);
  // The non-job choice carries its cost code's DB name ("Non-Job Time") when any row has it.
  const overhead = useMemo(() => namedNonJob(nonJob, baseRows.map((r) => r.job)), [nonJob, baseRows]);
  const on = day.state === 'in' || day.state === 'meal';
  const currentJob: PortalJob = on && day.openRow ? day.openRow.job : nextJob ?? overhead;

  const openRow = day.openRow;
  const otherDevice: SourceKind | null =
    openRow && openRow.id !== PENDING_ROW_ID && openRow.inSource !== 'web' && continuedRowID !== openRow.id && !pending
      ? openRow.inSource
      : null;
  const weekLock: 'submitted' | 'exported' | null = thisWeekStatus?.exported
    ? 'exported'
    : thisWeekStatus?.submitted || self?.submitted
      ? 'submitted'
      : null;
  const pendingLabel = pending ? pending.label : null;
  const blocked = !self
    ? loadError?.message ?? 'Loading your punches…'
    : blockedReason({
        sessionExpired,
        unavailable: loadError?.kind === 'unavailable' ? loadError.message : null,
        pendingLabel,
        otherDevice,
        weekLock,
      });
  const holidayToday = holidays.find((h) => (h.holidayDate ?? '').slice(0, 10) === today) ?? null;

  /* ------------------------------------------------ sending */

  const sendPunch = useCallback(async (p: PendingPunch) => {
    const sending: PendingPunch = { ...p, status: 'sending', attempts: p.attempts + 1 };
    setPending(sending);
    savePendingPunch(storage, sending);
    setRetryAt(null);
    const failed: PendingPunch = { ...sending, status: 'failed' };
    const label = p.label;
    const at = fmtTime(p.tapMs, zone);

    // Never send a punch without a token (the interceptor would send it bare → 401).
    const token = await getTokenStatus();
    if (token !== 'ok') {
      const offline: PendingPunch = { ...failed, lastError: 'network' };
      setPending(offline);
      savePendingPunch(storage, offline);
      if (token === 'expired') reportSessionExpired(punchNote(p, zone));
      else {
        // Offline: the silent token refresh works again once the connection is back.
        setRetryAt(Date.now() + RETRY_MS);
        if (p.attempts === 0) showToast(`${p.label} at ${fmtTime(p.tapMs, zone)} saved on this device — not sent yet`, 'warn');
      }
      return;
    }
    try {
      await sendPendingPunch(sending);
      clearPendingPunch(storage, p.employeeID, p.clientPunchID);
      // Reload first, then drop the overlay, so the hero never flashes the pre-punch state
      // (applyPendingPunch ignores a punch the server rows already show).
      await reloadSelf();
      setPending(null);
      if (p.action === 'clockIn') setNextJob(null);
      if (p.attempts > 0) showToast(`${label} at ${at} sent`);
      else if (p.action === 'jobSwitch') showToast(`Now on ${jobTitle(p.job)} — previous segment closed at ${at}`);
      else if (p.action === 'clockOut' && clockOutFlagged.current[p.clientPunchID]) {
        showToast(`Clocked out at ${at}. Today's meal will be a question when you submit the week.`, 'info', 5000);
      } else showToast(`${label} recorded at ${at}`);
      delete clockOutFlagged.current[p.clientPunchID];
    } catch (err) {
      const info = describePortalError(err);
      const next = punchFailureAction(info);
      if (next === 'signIn') {
        setPending(failed);
        savePendingPunch(storage, failed);
        reportSessionExpired(punchNote(p, zone));
        return;
      }
      if (next === 'keep') {
        // Network or 5xx: the write may or may not have landed. Keep the SAME clientPunchID
        // and body; a resend that already landed comes back as a replay, never a duplicate.
        const kept: PendingPunch = { ...failed, lastError: info.kind === 'server' ? 'server' : 'network' };
        setPending(kept);
        savePendingPunch(storage, kept);
        setRetryAt(Date.now() + RETRY_MS);
        if (p.attempts === 0) showToast(`${label} at ${at} saved on this device — not sent yet`, 'warn');
        return;
      }
      // A definite 4xx refusal: the write did not happen. Drop the local punch and show the server's truth.
      clearPendingPunch(storage, p.employeeID, p.clientPunchID);
      setPending(null);
      showToast(info.message, 'warn', 6500);
      if (info.code === 'WEEK_SUBMITTED' || info.code === 'PAYROLL_EXPORTED') await reload();
      else await reloadSelf();
    }
  }, [storage, setPending, zone, reportSessionExpired, reloadSelf, reload, showToast]);

  // Auto-retry a network failure with the SAME body + clientPunchID (never while signed out).
  useEffect(() => {
    if (!pending || pending.status !== 'failed' || retryAt == null || sessionExpired) return;
    const t = window.setTimeout(() => {
      if (pendingRef.current) void sendPunch(pendingRef.current);
    }, Math.max(0, retryAt - Date.now()));
    return () => window.clearTimeout(t);
  }, [pending, retryAt, sessionExpired, sendPunch]);

  // After "Sign in again", send what was waiting (a user-initiated sign-in, so a token exists).
  useEffect(() => onSessionRestored(() => {
    if (pendingRef.current) void sendPunch(pendingRef.current);
  }), [onSessionRestored, sendPunch]);

  /* ------------------------------------------------ recent activity (GET /portal/activity, this week) */
  const [activity, setActivity] = useState<PortalActivityItem[] | null>(null);
  const [activityFailed, setActivityFailed] = useState(false);
  const activitySeq = useRef(0);
  const loadActivity = useCallback(async () => {
    const mine = ++activitySeq.current;
    try {
      const res = await getPortalActivity({ weeks: 1 });
      if (mine !== activitySeq.current) return;
      setActivity(res.items);
      setActivityFailed(false);
    } catch (err) {
      if (mine !== activitySeq.current) return;
      if (describePortalError(err).kind === 'sessionExpired') reportSessionExpired();
      setActivityFailed(true);
    }
  }, [reportSessionExpired]);
  // Refetched whenever the employee reloads (after every punch that reached the server).
  useEffect(() => {
    if (self) void loadActivity();
  }, [self, loadActivity]);
  useEffect(() => onSessionRestored(() => { void loadActivity(); }), [onSessionRestored, loadActivity]);

  const retryNow = () => {
    if (pendingRef.current && pendingRef.current.status === 'failed') void sendPunch(pendingRef.current);
  };

  /* ------------------------------------------------ actions */
  const start = (action: PunchAction, tapMs: number, job: PortalJob | null) => {
    if (!self || blocked) return;
    const row = openRow && openRow.id !== PENDING_ROW_ID ? apiRows.get(openRow.id) ?? null : null;
    const label = actionLabel(action, deriveDay(baseRows, tapMs, zone, today).mealCount);
    let p: PendingPunch;
    if (action === 'clockIn') {
      const j = job ?? overhead;
      p = createPendingPunch({
        employeeID: self.employeeID, action, label, tapMs, rowID: null, job: j,
        method: 'post', url: PUNCH_URL.add, body: buildClockInBody(self.employeeID, tapMs, zone, j),
      });
    } else {
      if (!row) return;
      if (action === 'clockOut') {
        p = createPendingPunch({
          employeeID: self.employeeID, action, label, tapMs, rowID: row.timeEntryHourlyID, job: null,
          method: 'put', url: PUNCH_URL.update(row.timeEntryHourlyID), body: buildClockOutBody(row, tapMs, zone),
        });
        // The clock-out toast mentions the meal question when today has a flag (D4).
        const after = deriveDay(applyPendingPunch(baseRows, p), tapMs, zone, today);
        clockOutFlagged.current[p.clientPunchID] = todayMealFlags(after, rules).length > 0;
      } else {
        const kind = action === 'jobSwitch' ? 'jobSwitch' : action === 'mealStart' ? 'mealStart' : 'mealEnd';
        p = createPendingPunch({
          employeeID: self.employeeID, action, label, tapMs, rowID: row.timeEntryHourlyID, job: action === 'jobSwitch' ? job : null,
          method: 'post', url: PUNCH_URL.switch(row.timeEntryHourlyID), body: buildSwitchBody(kind, tapMs, job),
        });
      }
    }
    void sendPunch(p);
  };

  const onAction = (a: HeroAction) => {
    // Whole minutes, like mobile: the toast, the punch list and the stored row all read 8:18.
    const tap = floorToMinute(Date.now());
    switch (a) {
      case 'clockIn':
      case 'clockBackIn':
        start('clockIn', tap, jobCosting ? nextJob ?? overhead : overhead);
        break;
      case 'clockOut':
        start('clockOut', tap, null);
        break;
      case 'startMeal':
      case 'startSecondMeal':
        start('mealStart', tap, null);
        break;
      case 'endMeal':
        // Mobile validateMealTime: a meal under 30 minutes asks first.
        if (openRow && isShortMeal(openRow.inMs, tap)) setShortMealTap(tap);
        else start('mealEnd', tap, null);
        break;
    }
  };

  const onPickJob = (job: PortalJob) => {
    setPickerOpen(false);
    if (sameJob(job, currentJob)) return;
    if (day.state === 'in' && openRow) {
      if (!isSwitchableJob(job)) {
        toast.show(`${jobTitle(overhead)} has no cost code set up for you yet. Contact payroll.`, 'warn', 5000);
        return;
      }
      start('jobSwitch', floorToMinute(Date.now()), job);
    } else {
      setNextJob(job);
      toast.show(`You'll start on ${jobTitle(job)} when you clock in`, 'info');
    }
  };

  const continueHere = () => {
    if (!openRow) return;
    setContinuedRowID(openRow.id);
    try { storage?.setItem(CONTINUE_KEY, String(openRow.id)); } catch { /* ignore */ }
    toast.show('Continuing here — your next punch is recorded from Web');
  };

  // Gentle first-meal reminder (non-blocking toast), once per day.
  const remindNow = !!self && shouldRemindMeal(day, rules, cfg.firstMealReminderMinutes);
  useEffect(() => {
    if (!remindNow || !self) return;
    const key = `etp.mealReminder.${self.employeeID}.${today}`;
    try {
      if (storage?.getItem(key)) return;
      storage?.setItem(key, '1');
    } catch { /* ignore */ }
    toast.show(`You've worked ${fmtHours(day.workedMin)} h — take your meal before the end of your 5th hour`, 'info', 6000);
  }, [remindNow]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ------------------------------------------------ status strip */
  const stripKind = selectStrip({
    loading: loading && !self,
    pendingNetwork: !!pending && pending.status === 'failed' && !sessionExpired,
    loadError: !!loadError,
    otherDevice: !!otherDevice,
    weekLock,
    noSupervisor: !!thisWeekStatus?.noSupervisor,
    lastWeekOpen,
    holidayToday: !!holidayToday,
  });

  const renderStrip = () => {
    const S = (tone: string, Icon: LucideIcon, text: React.ReactNode, action?: React.ReactNode) => (
      <div className={`strip ${tone}`} role="status"><Icon /><span className="st-tx">{text}</span>{action}</div>
    );
    const weekRange = currentPeriod ? shortRange(currentPeriod.startYmd, currentPeriod.endYmd) : 'this week';
    switch (stripKind) {
      case 'network': {
        const secs = retryAt ? Math.max(0, Math.ceil((retryAt - nowMs) / 1000)) : 0;
        const sending = pending?.status === 'sending';
        return S('danger', WifiOff, (
          <><b>{pending?.lastError === 'server' ? "The server couldn't save it." : "Couldn't reach the server."}</b> Your {pendingLabel} at {pending ? fmtTime(pending.tapMs, zone) : ''} is saved on this device and
            sends on its own — next try in <span className="mono">0:{String(secs).padStart(2, '0')}</span>.</>
        ), (
          <button type="button" className="btn sm" onClick={retryNow} disabled={sending}><RefreshCw />{sending ? 'Sending…' : 'Retry now'}</button>
        ));
      }
      case 'loadError':
        return S('danger', loadError?.kind === 'unavailable' ? Lock : WifiOff, (
          <><b>{loadError?.kind === 'unavailable' ? 'The time portal is unavailable.' : "Couldn't load your time."}</b> {loadError?.message}</>
        ), (
          <button type="button" className="btn sm" onClick={() => void reload()}><RefreshCw />Retry</button>
        ));
      case 'loading':
        return S('ok', Clock, <>Loading your time…</>);
      case 'otherDevice':
        return S('info', otherDevice === 'kiosk' ? Tablet : Smartphone, (
          <><b>You're clocked in on the {SOURCE_LABEL[otherDevice ?? 'mobile'].toLowerCase()}</b> since {openRow ? fmtTime(openRow.inMs, zone) : ''}.
            Continue the same shift here — your next punch is recorded from Web.</>
        ), (
          <button type="button" className="btn sm primary" onClick={continueHere}>Continue here</button>
        ));
      case 'locked':
        if (weekLock === 'exported') {
          return S('warn', Lock, (
            <><b>Payroll for this week ({weekRange}) was exported</b>, so punches are locked. Contact payroll to make changes.</>
          ));
        }
        return S('warn', Lock, (
          <><b>This week ({weekRange}) was submitted</b>
            {thisWeekStatus?.submittedAt ? <> on {fmtDayShort(Date.parse(thisWeekStatus.submittedAt), zone)} at {fmtTime(Date.parse(thisWeekStatus.submittedAt), zone)}</> : null}
            , so punches are locked. Ask your supervisor or payroll to unlock this week.</>
        ));
      case 'noSupervisor': {
        const c = cfg.payrollContact;
        const contact = [c?.email, c?.phone].filter(Boolean).join(' or ');
        return S('danger', UserX, (
          <><b>No supervisor is assigned to you.</b> Contact payroll{contact ? ` at ${contact}` : ''} so your timesheets reach an approver.</>
        ), c?.email ? <a className="btn sm" href={`mailto:${c.email}`}><Mail />Email payroll</a> : undefined);
      }
      case 'lastWeekOpen':
        return S('info', Send, (
          <><b>Last week ({previousPeriod ? shortRange(previousPeriod.startYmd, previousPeriod.endYmd) : ''}) isn't submitted.</b>
            {previousPeriod?.checkYmd ? <> Submit it so it makes the {fmtYmdDow(previousPeriod.checkYmd)} check.</> : <> Submit it when you're ready.</>}</>
        ), (
          <button
            type="button"
            className="btn sm primary"
            onClick={() => (previousPeriod ? openTimesheet(previousPeriod.payPeriodID, true) : setPage(Page.MyTimesheet))}
          >
            Review &amp; submit
          </button>
        ));
      case 'holiday': {
        const eligible = holidayToday?.eligibility ? holidayToday.eligibility.isEligible : true;
        return S('holiday', PartyPopper, (
          <><b>Today is {holidayToday?.name}</b> — a company holiday.{' '}
            {eligible
              ? <>{Number(holidayToday?.paidHoursIfNotWorked ?? 0).toFixed(2)} h holiday pay comes from the company calendar; punch only if you're working.</>
              : <>Holiday pay doesn't apply to you yet; punch if you're working.</>}</>
        ));
      }
      case 'caughtUp':
      default: {
        const paid = lastWeekStatus && previousPeriod?.checkYmd
          ? <> {fmtYmdDow(previousPeriod.checkYmd)} pays {shortRange(previousPeriod.startYmd, previousPeriod.endYmd)}.</>
          : null;
        return S('ok', CheckCircle2, <><b>You're all caught up.</b>{paid}</>);
      }
    }
  };

  /* ------------------------------------------------ hero */
  const failedPending = pending?.status === 'failed' ? pending : null;
  /** "10:00 PM", or "Mon 10:00 PM" when the instant is on an earlier day (a shift that crossed midnight). */
  const sinceLabel = (ms: number) =>
    localYmd(ms, zone) === today ? fmtTime(ms, zone) : `${fmtDayShort(ms, zone).split(',')[0]} ${fmtTime(ms, zone)}`;
  const pill = (() => {
    if (failedPending) {
      return <span className="hstat warn"><span className="dot" />{pendingLabel} {fmtTime(failedPending.tapMs, zone)} · not sent yet</span>;
    }
    if (!self) return <span className="hstat out"><span className="dot" />{loading ? 'Loading' : 'Not available'}</span>;
    switch (day.state) {
      case 'meal': {
        const start = day.mealStarts[day.mealStarts.length - 1];
        return <span className="hstat meal"><span className="dot" />On meal · since {start != null ? sinceLabel(start) : ''}</span>;
      }
      case 'done':
        return <span className="hstat out"><span className="dot" />Clocked out · {day.lastOutMs != null ? fmtTime(day.lastOutMs, zone) : ''}</span>;
      case 'out':
        return <span className="hstat out"><span className="dot" />Not clocked in</span>;
      default:
        return <span className="hstat on"><span className="dot" />Clocked in · since {day.firstInMs != null ? sinceLabel(day.firstInMs) : ''}</span>;
    }
  })();

  const shiftSpanMin = day.firstInMs != null ? ((day.lastOutMs ?? nowMs) - day.firstInMs) / 60000 : 0;
  const stale = day.staleOpenRow;
  const openStaleWeek = () => {
    const week = stale ? periodContaining(periods, localYmd(stale.inMs, zone)) : null;
    if (week) openTimesheet(week.payPeriodID);
    else setPage(Page.MyTimesheet);
  };
  const sub = day.state === 'out'
    ? <>{stale
        ? <>Open punch from <b>{fmtDayShort(stale.inMs, zone)}</b> has no clock out —{' '}
            <button type="button" className="linkbtn" style={{ fontSize: 'inherit' }} onClick={openStaleWeek}>add it in My Timesheet</button></>
        : <>Punches here are recorded as <b>Web</b></>}</>
    : <>Worked <b>{fmtHours(day.workedMin)} h</b> {day.carriedOver ? 'this shift' : 'today'} · meal of <b>{Math.round(day.mealMin)} min</b> excluded · shift span <b>{fmtHours(shiftSpanMin)} h</b></>;

  const actions = heroActions(day.state, day.mealCount, day.workedMin, cfg.secondMealUnlockMinutes);
  const disabled = !!blocked;
  const ACTION_UI: Record<HeroAction, { cls: string; Icon: LucideIcon; label: string }> = {
    clockIn: { cls: '', Icon: Play, label: 'Clock In' },
    clockBackIn: { cls: '', Icon: Play, label: 'Clock Back In' },
    clockOut: { cls: 'stop', Icon: Square, label: 'Clock Out' },
    startMeal: { cls: 'meal', Icon: Coffee, label: 'Start Meal' },
    startSecondMeal: { cls: 'meal', Icon: Coffee, label: 'Start 2nd Meal' },
    endMeal: { cls: '', Icon: Play, label: 'End Meal' },
  };
  const hint = blocked || complianceHint(day, rules, zone);
  // Today's punches with the job switches between them, in time order (a switch sorts after a punch at the same minute).
  const punchList: Array<(PunchEvent & { to?: undefined }) | { key: string; kind: 'switch'; atMs: number; source: SourceKind; pending: boolean; to: PortalJob }> = [
    ...day.events,
    ...day.switches.map((sw) => ({ key: sw.key, kind: 'switch' as const, atMs: sw.atMs, source: sw.source, pending: sw.pending, to: sw.to })),
  ].sort((a, b) => a.atMs - b.atMs || (a.kind === 'switch' ? 1 : 0) - (b.kind === 'switch' ? 1 : 0));
  const clock = clockParts(nowMs, zone);

  // Today's punches keep the newest (bottom) row in view as punches arrive and on load —
  // unless the employee scrolled up to read older ones.
  const punchListRef = useRef<HTMLDivElement | null>(null);
  const stickToNewest = useRef(true);
  const onPunchListScroll = () => {
    const el = punchListRef.current;
    if (el) stickToNewest.current = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
  };
  const punchListKey = `${punchList.length}:${punchList[punchList.length - 1]?.key ?? ''}:${on ? 1 : 0}`;
  useEffect(() => {
    const el = punchListRef.current;
    if (el && stickToNewest.current) el.scrollTop = el.scrollHeight;
  }, [punchListKey]);

  const hero = (
    <div className="hero">
      <div className="hero-in">
        <div className="hero-l">
          {pill}
          <div className="clockface">{clock.hm}<span className="cf-s">{clock.ampm}</span></div>
          <div className="hsub">{sub}</div>
          <div className="hero-acts">
            {actions.map((a) => {
              const ui = ACTION_UI[a];
              return (
                <button key={a} type="button" className={`pbtn ${ui.cls}`} disabled={disabled} onClick={() => onAction(a)}>
                  <ui.Icon />{ui.label}
                </button>
              );
            })}
          </div>
          <div className={`hero-hint${blocked ? ' lock' : ''}`}>
            {blocked ? <Lock /> : <Info />}<span>{hint}</span>
          </div>
        </div>
        <div className="hero-r">
          <div className="hr-lab">Today's punches</div>
          <div className="hr-list" ref={punchListRef} onScroll={onPunchListScroll}>
            {punchList.length === 0 && <div className="hr-empty">No punches yet today</div>}
            {punchList.map((e) => {
              const Icon = e.kind === 'switch' ? Briefcase : KIND_ICON[e.kind];
              return (
                <div key={e.key} className={`hr-punch${e.pending ? ' pending' : ''}`}>
                  <span className="pk"><Icon /></span>
                  <span className="pl">
                    {e.kind === 'switch'
                      ? <span className="pj" title={`Switched to ${jobTitle(e.to)}`}>Switched to {jobTitle(e.to)}</span>
                      : PUNCH_LABEL[e.kind]}
                    {e.pending
                      ? <span className="pend"><WifiOff />{pending?.status === 'failed' ? 'Not sent' : 'Sending'}</span>
                      // A switch row spends the chip's room on the job name (same source as the punch beside it).
                      : e.kind === 'switch' ? null : <SrcChip source={e.source} short />}
                  </span>
                  <span className="pt">{fmtTime(e.atMs, zone)}</span>
                </div>
              );
            })}
            {on && (
              <div className="hr-punch now">
                <span className="pk"><Clock /></span>
                <span className="pl">{day.state === 'meal' ? 'On meal' : 'Working'}</span>
                <span className="pt">{fmtTime(nowMs, zone)}</span>
              </div>
            )}
          </div>
          <div className="hr-foot"><span className="fl">Worked</span><span className="fv">{fmtHours(day.workedMin)}</span></div>
        </div>
      </div>
    </div>
  );

  /* ------------------------------------------------ cards */
  const card = (Icon: LucideIcon, title: string, subLine: string | null, body: React.ReactNode, right?: React.ReactNode) => (
    <div className="card">
      <div className="card-head">
        <span className="ch-ic"><Icon /></span>
        <div className="ch-tx"><h3>{title}</h3>{subLine ? <div className="ch-sub">{subLine}</div> : null}</div>
        {right ? <div className="right">{right}</div> : null}
      </div>
      {body}
    </div>
  );

  const switchDisabled = disabled || day.state === 'meal';
  const jobCard = jobCosting
    ? card(Briefcase, 'Working on', on ? 'Applies until you switch' : 'Starts when you clock in', (
      <div className="cbody jc2">
        <div className="jc-cur">
          <span className="jic">{isOverhead(currentJob) ? <Home /> : <Building2 />}</span>
          <span className="jc-tx">
            <span className="jt">{jobTitle(currentJob)}</span>
            <span className="js">{jobSub(currentJob)}</span>
          </span>
          <button
            type="button"
            className="btn sm chg"
            disabled={switchDisabled}
            title={day.state === 'meal' ? 'End your meal to switch jobs' : undefined}
            onClick={() => setPickerOpen(true)}
          >
            <RefreshCw />Switch
          </button>
        </div>
        <div className="mini-lab">Today's segments</div>
        <div className="jc-segs">
          {day.segments.length === 0 && <div className="pk-empty">No time yet today</div>}
          {[...day.segments].reverse().map((s) => (
            <div key={s.key} className={`sg-row${s.live ? ' live' : ''}${s.meal ? ' meal' : ''}`}>
              <span className="sg-t">{fmtTime(s.fromMs, zone)} – {s.live ? 'now' : s.toMs != null ? fmtTime(s.toMs, zone) : ''}</span>
              {s.meal
                ? <span className="sg-j">Meal<small>unpaid · {Math.floor(s.workedMin)} min</small></span>
                : <span className="sg-j">{jobTitle(s.job)}<small>{isOverhead(s.job) ? 'Not job related' : jobCode(s.job)}</small></span>}
              <span className="sg-h">{s.meal ? '—' : (s.hundredths / 100).toFixed(2)}</span>
            </div>
          ))}
        </div>
      </div>
    ))
    : null;

  const upcoming = buildUpcoming({ todayYmd: today, periods, requests, holidays, supervisorName: thisWeekStatus?.supervisorName ?? null });
  const upCard = card(Calendar, 'Coming up', null, (
    <div className="cbody pad">
      {upcoming.length === 0 && <div className="pk-empty">Nothing coming up</div>}
      {upcoming.map((r) => {
        const t = ymdTile(r.ymd);
        return (
          <div key={r.key} className="up-row">
            <span className="ud"><span className="um">{t.mon}</span><span className="un">{t.day}</span></span>
            <span className="ut"><b>{r.title}</b><span>{r.sub}</span></span>
          </div>
        );
      })}
    </div>
  ));

  // This week's feed (latest 5), with a punch still waiting to send on top (it isn't on the server yet).
  const unsent: ActivityItem[] = todaysActivity(day).filter((a) => a.pending);
  const recent = (activity ?? []).slice(0, Math.max(0, RECENT_ACTIVITY_COUNT - unsent.length));
  const recentCard = card(History, 'Recent activity', null, (
    <div className="cbody act-mini">
      {unsent.map((a) => (
        <div key={a.key} className="aud-row">
          <span className="aud-ic warn">{a.delta ? <Briefcase /> : <Clock />}</span>
          <span className="aud-b">
            <span className="at">{a.title}</span>
            <span className="as" style={{ display: 'block' }}>Today {fmtTime(a.atMs, zone)} · You · Not sent yet</span>
            {a.delta && <DeltaChip before={a.delta.was} after={a.delta.now} />}
          </span>
        </div>
      ))}
      {recent.map((item) => <ActivityRow key={item.id} item={item} zone={zone} todayYmd={today} mini />)}
      {unsent.length === 0 && recent.length === 0 && (
        <div className="pk-empty">
          {activityFailed ? (
            <>Couldn't load your activity<button type="button" className="linkbtn" onClick={() => void loadActivity()}>Try again</button></>
          ) : activity == null ? 'Loading…' : 'No activity yet this week'}
        </div>
      )}
    </div>
  ), <button type="button" className="linkbtn" onClick={() => setPage(Page.MyActivity)}>Full log</button>);

  /* ------------------------------------------------ your day */
  const timeline = card(BarChart3, 'Your day', '6:00 AM – 8:00 PM', (
    <div className="tlwrap">
      <div className="tl">
        {day.rows.map((r) => {
          const left = pct(r.inMs, zone);
          const end = r.outMs ?? nowMs;
          const width = Math.max(0, pct(end, zone) - left);
          const cls = r.type === 2 ? 'meal' : r.outMs == null ? 'live' : 'work';
          return <div key={`tl-${r.id}-${r.inMs}`} className={`tl-seg ${cls}`} style={{ left: `${left}%`, width: `${width}%` }} />;
        })}
        {on && <div className="tl-now" style={{ left: `${pct(nowMs, zone)}%` }} />}
      </div>
      <div className="tl-ax">{['6 AM', '9 AM', '12 PM', '3 PM', '6 PM', '8 PM'].map((t) => <span key={t}>{t}</span>)}</div>
      <div className="tl-key">
        <span><i style={{ background: 'color-mix(in srgb,var(--ok) 78%,#fff)' }} />Worked</span>
        <span><i style={{ background: 'color-mix(in srgb,var(--c-ot) 80%,#fff)' }} />Meal (unpaid)</span>
      </div>
    </div>
  ));

  /* ------------------------------------------------ page */
  const firstName = self?.name.first || self?.selfEmployee.firstName || userName.split(' ')[0] || '';
  const greet = greetingFor(localMinutes(nowMs, zone));
  const subLine = [fmtDateLong(nowMs, zone), self?.role?.name, 'Hourly'].filter(Boolean).join(' · ');

  return (
    <div className="etp">
      <div className="page-head">
        <div className="ph-ic"><Clock /></div>
        <div className="ph-tx">
          <h1>{greet}{firstName ? `, ${firstName}` : ''}</h1>
          <div className="ph-sub">{subLine}</div>
        </div>
        <div className="ph-actions">
          <button type="button" className="btn" onClick={() => setPage(Page.MyTimesheet)}><ClipboardCheck />My Timesheet</button>
          <button type="button" className="btn" onClick={() => setPage(Page.MyTimeOff)}><CalendarCheck />Request Time Off</button>
        </div>
      </div>

      {renderStrip()}

      <div className="stack">
        {hero}
        <div className={`${jobCard ? 'grid3' : 'grid2e'} fixrow`}>
          {jobCard}
          {upCard}
          {recentCard}
        </div>
        {timeline}
      </div>

      {pickerOpen && self && (
        <JobPicker
          currentJob={currentJob}
          overhead={overhead}
          overheadLabel={jobTitle(overhead)}
          employeeID={self.employeeID}
          subLine={on ? `Switching closes your current segment at ${fmtTime(nowMs, zone)} and starts a new one` : 'Applies when you clock in'}
          onPick={onPickJob}
          onClose={() => setPickerOpen(false)}
          onSessionExpired={() => { setPickerOpen(false); reportSessionExpired(); }}
        />
      )}

      {shortMealTap != null && (
        <div className="etp-modal-bg" role="presentation">
          <div className="etp-modal" role="alertdialog" aria-modal="true" aria-labelledby="etp-short-meal-title">
            <div className="modal-head">
              <div className="mi warn"><Coffee /></div>
              <div>
                <h3 id="etp-short-meal-title">Meal Time Warning</h3>
                <div className="ms">Your meal started at {openRow ? fmtTime(openRow.inMs, zone) : ''}</div>
              </div>
            </div>
            <div className="modal-body"><p className="mtext">{SHORT_MEAL_MESSAGE}</p></div>
            <div className="modal-foot">
              <button type="button" className="btn" onClick={() => setShortMealTap(null)}>No, keep my meal</button>
              <button
                type="button"
                className="btn primary"
                onClick={() => { const tap = shortMealTap; setShortMealTap(null); start('mealEnd', tap, null); }}
              >
                Yes, end my meal
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.node}
    </div>
  );
};

export default TodayPage;
